import {
  DialogContentButton,
  DialogContentText, DialogDownloadCloseButton,
} from "./downloadModal.styled";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide, Typography,
} from "@mui/material";
import {CloseModalIcon} from "../../assets/svg/closeModalIcon";
import React, {forwardRef, PropsWithChildren, useEffect, useImperativeHandle, useState} from "react";
import {TransitionProps} from "@mui/material/transitions";
import Api from "../api/api";
import {useMutation} from "@tanstack/react-query";
import {decryptFile} from "../../utils/fileAction";
import {UiGradientInput} from "../ui/includes/GradientInput";
import {BlueDownloadIcon} from "../../assets/svg/blueDownloadIcon";

export interface UploadModalRef {
  openModal: () => void;
  closeModal: () => void;
  fileId?: string;
  fileName?: string;
}

interface DownloadModalProps {
  fileId?: string;
  fileName?: string;
}

const Transition = forwardRef(function Transition(
  props: PropsWithChildren<TransitionProps> & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const api = Api();

export const DownloadModal = forwardRef<UploadModalRef, DownloadModalProps>(({ fileId, fileName }, ref) => {
  const [openModal, setOpenModal] = useState(false);
  const [secretPhrase, setSecretPharse] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useImperativeHandle(ref, () => ({
    openModal: () => setOpenModal(true),
    closeModal: () => setOpenModal(false)
  }));

  const { mutateAsync } = useMutation({
    mutationKey: ['encodeFile', secretPhrase],
    mutationFn: async () => {
      if (!fileId || !fileName) {
        throw new Error('Please select a file and enter a secret phrase.');
      }

      const filebuffer = await api.downloadFile(fileId);

      if (!secretPhrase) {
        await api.downloadBlob(filebuffer, fileName);
        return;
      }

      try {
        const decryptedFileBuffer = await decryptFile(filebuffer, secretPhrase);

        if (!decryptedFileBuffer || decryptedFileBuffer.byteLength === 0) {
          setErrorMessage('Incorrect encryption key');
          return;
        }

        const decryptFileName = fileName.split('.').slice(0, -1).join('.');
        await api.downloadBlob(decryptedFileBuffer, decryptFileName);

        setOpenModal(false);
      } catch (error) {
        setErrorMessage('Incorrect encryption key');
        return;
      }
    },
    onError: (error) => {
      if (error) {
        setErrorMessage('Incorrect encryption key')
      }
    },
  });

  useEffect(() => {
    if (openModal) {
      setErrorMessage('')
      setSecretPharse('')
    }
  }, [openModal]);

  useEffect(() => {
    setErrorMessage('')
  }, [secretPhrase]);

  const handleSecretPhraseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSecretPharse(e.target.value);
  };

  const handleDownloadFile = () => {
    mutateAsync();
  };

  return (
    <>
      <Dialog
        open={openModal}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setOpenModal(false)}
        aria-describedby="alert-dialog-slide-description"
      >

        <DialogTitle sx={{ fontSize: '56px', p: '0', lineHeight: 1 }}>Download <br />File{' '}<BlueDownloadIcon/></DialogTitle>
        <DialogActions>
          <DialogDownloadCloseButton onClick={() => setOpenModal(false)}><CloseModalIcon /></DialogDownloadCloseButton>
        </DialogActions>

          <DialogContent>
              <Typography mb="12px">Secret phrase</Typography>
              <UiGradientInput
                placeholder={'Anything...'}
                onChange={handleSecretPhraseChange}
                value={secretPhrase}
                />
              {errorMessage && <Typography fontSize={'12px'} my={'16px'} color={'red'}>{errorMessage}</Typography>}
              <DialogContentText>File has been encrypted with AES-GCM encryption method.</DialogContentText>
            <DialogContentButton disabled={!secretPhrase} variant={'contained'} onClick={handleDownloadFile}>Download file</DialogContentButton>
          </DialogContent>
      </Dialog>
    </>
  )
})
